import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, FileText, Clock, Calendar, AlertCircle, CheckCircle, TrendingUp } from "lucide-react";
import RiepilogoAssenzePanel from "./RiepilogoAssenzePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminHome({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [usersRes, requestsRes, reportsRes] = await Promise.allSettled([
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/richieste-assenze`, { headers }),
        axios.get(`${API}/reports?year=${new Date().getFullYear()}&month=${new Date().getMonth() + 1}`, { headers })
      ]);

      const users = usersRes.status === 'fulfilled' ? usersRes.value.data : [];
      const requests = requestsRes.status === 'fulfilled' ? requestsRes.value.data : [];
      const reports = reportsRes.status === 'fulfilled' ? reportsRes.value.data : [];

      // Build user lookup map
      const userMap = {};
      users.forEach(u => {
        userMap[u.id] = `${u.last_name || ''} ${u.first_name || ''}`.trim() || u.username;
      });

      const activeUsers = users.filter(u => u.account_status !== 'disabled');
      const operators = activeUsers.filter(u => u.role === 'operatore');
      const coordinators = activeUsers.filter(u => u.is_coordinatore);
      const pending = requests.filter(r => r.stato === 'pending');

      // Enrich requests with user names
      const enrichedRequests = requests.map(r => ({
        ...r,
        user_name: userMap[r.user_id] || r.user_name || 'Operatore'
      }));
      const enrichedPending = enrichedRequests.filter(r => r.stato === 'pending');

      setStats({
        totalOperators: operators.length,
        totalCoordinators: coordinators.length,
        totalUsers: activeUsers.length,
        pendingRequests: pending.length,
        reportsThisMonth: reports.length,
        approvedRequests: requests.filter(r => r.stato === 'approved').length,
        rejectedRequests: requests.filter(r => r.stato === 'rejected').length
      });

      setPendingRequests(enrichedPending.slice(0, 5));

      const recent = enrichedRequests
        .filter(r => r.stato !== 'pending')
        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
        .slice(0, 6);
      setRecentActivity(recent);

    } catch {
      // fallback to empty stats
      setStats({ totalOperators: 0, totalCoordinators: 0, totalUsers: 0, pendingRequests: 0, reportsThisMonth: 0, approvedRequests: 0, rejectedRequests: 0 });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-home">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md bg-gradient-to-br from-cyan-50 to-cyan-100/50 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onNavigate('users')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-cyan-600 uppercase tracking-wider">Operatori</p>
                <p className="text-3xl font-bold text-cyan-900 mt-1">{stats?.totalOperators || 0}</p>
                <p className="text-xs text-cyan-600 mt-1">{stats?.totalCoordinators || 0} coordinatori</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-cyan-600/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-amber-100/50 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onNavigate('reports')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">Report Mese</p>
                <p className="text-3xl font-bold text-amber-900 mt-1">{stats?.reportsThisMonth || 0}</p>
                <p className="text-xs text-amber-600 mt-1">questo mese</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-600/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer ${stats?.pendingRequests > 0 ? 'bg-gradient-to-br from-red-50 to-red-100/50' : 'bg-gradient-to-br from-emerald-50 to-emerald-100/50'}`} onClick={() => onNavigate('turni')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${stats?.pendingRequests > 0 ? 'text-red-600' : 'text-emerald-600'}`}>In Attesa</p>
                <p className={`text-3xl font-bold mt-1 ${stats?.pendingRequests > 0 ? 'text-red-900' : 'text-emerald-900'}`}>{stats?.pendingRequests || 0}</p>
                <p className={`text-xs mt-1 ${stats?.pendingRequests > 0 ? 'text-red-600' : 'text-emerald-600'}`}>richieste assenza</p>
              </div>
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${stats?.pendingRequests > 0 ? 'bg-red-600/10' : 'bg-emerald-600/10'}`}>
                <AlertCircle className={`h-6 w-6 ${stats?.pendingRequests > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-violet-50 to-violet-100/50 hover:shadow-lg transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-violet-600 uppercase tracking-wider">Gestite</p>
                <p className="text-3xl font-bold text-violet-900 mt-1">{(stats?.approvedRequests || 0) + (stats?.rejectedRequests || 0)}</p>
                <p className="text-xs text-violet-600 mt-1">{stats?.approvedRequests || 0} approvate, {stats?.rejectedRequests || 0} rifiutate</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-violet-600/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Riepilogo Assenze (sopra "In Attesa di Approvazione") */}
      <RiepilogoAssenzePanel />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Richieste in attesa */}
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Richieste in Attesa
              </CardTitle>
              {stats?.pendingRequests > 0 && (
                <Button variant="ghost" size="sm" className="text-cyan-600 text-xs" onClick={() => onNavigate('turni')}>
                  Vedi tutte
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {pendingRequests.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-gray-400">
                <CheckCircle className="h-10 w-10 mb-2 text-emerald-400" />
                <p className="text-sm font-medium text-emerald-600">Nessuna richiesta in attesa</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingRequests.map((req) => (
                  <div key={req.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-amber-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center text-xs font-bold text-white ${req.tipo === 'ferie' ? 'bg-teal-500' : req.tipo === 'permesso' ? 'bg-violet-500' : 'bg-amber-500'}`}>
                        {req.tipo === 'ferie' ? 'F' : req.tipo === 'permesso' ? 'P' : 'A'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{req.user_name || 'Operatore'}</p>
                        <p className="text-xs text-gray-500 capitalize">{req.tipo} &middot; {formatDate(req.data_inizio)} - {formatDate(req.data_fine)}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                      onClick={() => onNavigate('turni')}
                    >
                      Gestisci
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Attivita recente */}
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4 text-cyan-500" />
              Attivita Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-gray-400">
                <Calendar className="h-10 w-10 mb-2" />
                <p className="text-sm">Nessuna attivita recente</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                    <div className={`h-2 w-2 rounded-full shrink-0 ${item.stato === 'approved' ? 'bg-emerald-500' : 'bg-red-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">
                        <span className="font-medium">{item.user_name || 'Operatore'}</span>
                        <span className="text-gray-500"> &middot; {item.tipo}</span>
                      </p>
                      <p className="text-xs text-gray-400">{formatDate(item.data_inizio)} - {formatDate(item.data_fine)}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.stato === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {item.stato === 'approved' ? 'Approvata' : 'Rifiutata'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Navigation */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Accesso Rapido</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Turni', value: 'turni', icon: Calendar, color: 'cyan' },
              { label: 'Report', value: 'reports', icon: FileText, color: 'amber' },
              { label: 'Utenti', value: 'users', icon: Users, color: 'violet' },
              { label: 'Procedure', value: 'procedure', icon: FileText, color: 'emerald' }
            ].map(item => (
              <button
                key={item.value}
                onClick={() => onNavigate(item.value)}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 border-transparent hover:border-${item.color}-200 bg-${item.color}-50/50 hover:bg-${item.color}-50 transition-all text-left group`}
                data-testid={`quick-nav-${item.value}`}
              >
                <item.icon className={`h-5 w-5 text-${item.color}-600`} />
                <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{item.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
